import { HOST_TULUM, API, HOST } from '@/utils/http'
import { toast } from "@/components/ui/toast/use-toast"

const layerToFeature = (layer) => {
  return {
    id: layer.id,
    uuid: layer.code,
    type: 'Feature',
    geometry: layer.layer?.geometry ? layer.layer.geometry : null,
    properties: {
      type: layer.type,
      visible: false,
      code: layer.code,
      name: layer.name,
      organization_id: layer.org_id,
      layer: layer.layer,
      created_at: layer.created_at,
      updated_at: layer.updated_at
    }
  }
}

// Layer type 2 = subscription layer, status 0 = done
const SUBSCRIPTION_LAYER_TYPE = 2
const STATUS_DONE = 0

export const fetchSubscriptionResults = async ({ commit }, { orgId, layerCodes }) => {
  if (!layerCodes.length) return {}

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers/subscription/results/batch`,
      data: { layer_codes: layerCodes }
    })

    commit('SET_SUBSCRIPTION_RESULTS', { orgId, results: response.data })
    return response.data
  } catch (error) {
    console.error('Error fetching subscription results:', error)
    return {}
  }
}

export const fetchOrgLayers = async ({ commit, dispatch }, { orgId }) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/layers/all`
    })

    const layers = response.data
      .map(layerToFeature)
      .sort((a, b) => new Date(b.properties.created_at) - new Date(a.properties.created_at))

    commit('SET_ORG_LAYERS', { orgId, layers })

    // Batch fetch subscription results for completed subscription layers
    const subscriptionLayers = layers.filter(
      l => l.properties.type === SUBSCRIPTION_LAYER_TYPE && l.properties.layer?.status === STATUS_DONE
    )
    const layerCodes = subscriptionLayers.map(l => l.properties.code)

    if (layerCodes.length > 0) {
      dispatch('fetchSubscriptionResults', { orgId, layerCodes })
    }

    return layers
  } catch (error) {
    console.error('Error fetching organization layers:', error)
  }
}

export const addOrgLayer = ({ commit }, { orgId, layer }) => {
  const featureLayer = layerToFeature(layer)
  commit('ADD_ORG_LAYER', { orgId, layer: featureLayer })
  return featureLayer
}

export const updateOrgLayer = ({ state,commit }, { orgId, layer }) => {
  commit('UPDATE_ORG_LAYER', { orgId, layer })
  return layer
}

export const updateOrgLayerFeatures = ({ commit }, { orgId, layerId, features }) => {
  commit('UPDATE_ORG_LAYER_FEATURES', { orgId, layerId, features })
  return features
}

export const moveOrgLayerUp = ({ commit }, { orgId, layerId }) => {
  commit('MOVE_ORG_LAYER_UP', { orgId, layerId })
}

export const moveOrgLayerDown = ({ commit }, { orgId, layerId }) => {
  commit('MOVE_ORG_LAYER_DOWN', { orgId, layerId })
}

export const reorderOrgLayers = ({ commit }, { orgId, layers }) => {
  commit('REORDER_ORG_LAYERS', { orgId, layers })
}

export const renameOrgLayer = async ({ commit }, { orgId, layerId, name }) => {
  try {
    const trimmedName = typeof name === 'string' ? name.trim() : ''
    if (!orgId || !layerId || !trimmedName) {
      throw new Error('Missing required parameters: orgId, layerId, and name are required')
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/layers/${layerId}/name`,
      data: { name: trimmedName }
    })

    commit('RENAME_ORG_LAYER', { orgId, layerId, name: trimmedName })
    return true
  } catch (error) {
    console.error('Error renaming organization layer:', error)
    throw error
  }
}

export const deleteOrgLayer = async ({ commit }, { orgId, layerId }) => {
  try {
    // Validate parameters
    if (!orgId || !layerId) {
      throw new Error('Missing required parameters: orgId and layerId are required')
    }

    // Log deletion attempt for debugging
    console.warn('DELETING LAYER:', {
      orgId,
      layerId,
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    })

    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/layers/${layerId}`
    })

    commit('DELETE_ORG_LAYER', { orgId, layerId })
    return true
  } catch (error) {
    console.error('Error deleting organization layer:', error)
    throw error
  }
}

export const setSelectedFeature = ({ commit }, { feature, coordinates, isVisible }) => {
  commit('SET_SELECTED_FEATURE', { feature, coordinates, isVisible })
}

export const fetchLayers = async ({commit}, payload) => {
  const response = await API({
    method: 'GET',
    url: `${HOST_TULUM}/layers/${payload.org_id}`
  })
}

export const updateLayerProperties = ({ commit, rootState }, { orgId, layerId, properties }) => {
  const resolvedOrgId = orgId || rootState.org?.selectedOrg?.id
  if (!resolvedOrgId || !layerId) {
    console.warn('updateLayerProperties: missing orgId or layerId', { orgId, layerId })
    return
  }

  commit('UPDATE_LAYER_PROPERTIES', { orgId: resolvedOrgId, layerId, properties })
}

export const setActiveResult = ({ commit }, { layerId, result }) => {
  commit('SET_ACTIVE_RESULT', { layerId, result })
}

export const setPlanetscopeSelection = ({ commit }, { layerId, selection }) => {
  commit('SET_PLANETSCOPE_SELECTION', { layerId, selection })
}

export const setOrderProductBundle = ({ commit }, { layerId, bundle }) => {
  commit('SET_ORDER_PRODUCT_BUNDLE', { layerId, bundle })
}

export const createProjectDataLayer = async ({ commit, getters }, { orgId, projectId, projectName }) => {
  const trimmedName = typeof projectName === 'string' ? projectName.trim() : ''
  if (!orgId || !projectId || !trimmedName) {
    throw new Error('Missing required parameters: orgId, projectId, and projectName are required')
  }

  // Check for existing layer with same project filter
  const existingLayers = getters.getOrgLayers(orgId)
  const duplicate = existingLayers.find(layer => {
    if (layer.properties?.type !== 4) return false
    const filters = layer.properties?.layer?.data?.filter?.config
    if (!Array.isArray(filters) || filters.length !== 1) return false
    const f = filters[0]
    return f.type === 'ArrayFilter' && f.field_name === 'project_id' &&
      Array.isArray(f.config) && f.config.length === 1 && String(f.config[0]) === String(projectId)
  })

  if (duplicate) {
    toast({ title: 'Layer already exists', description: `A Project Data layer for "${trimmedName}" already exists.` })
    return duplicate
  }

  const response = await API({
    method: 'POST',
    url: `${HOST}/org/${orgId}/layers`,
    data: {
      org_id: orgId,
      type: 4,
      name: trimmedName,
      layer: {
        aoi: null,
        data: {
          filter: {
            type: 'AndFilter',
            config: [{
              type: 'ArrayFilter',
              field_name: 'project_id',
              config: [String(projectId)]
            }]
          }
        }
      }
    }
  })

  const featureLayer = layerToFeature(response.data)
  commit('ADD_ORG_LAYER', { orgId, layer: featureLayer })
  toast({ title: 'Layer created', description: `"${trimmedName}" added to Layers.` })
  return featureLayer
}
